import {
	deleteUser,
	getUserByUid,
	getUserDashboardData,
	sendEmail,
	setUser
} from '../../../api/modules/api-user'
import { runInAction } from 'mobx'
import { wrapWithLoading } from '../../../config/general-actions'
import root from '../../'
import { setRecruitment } from '../../../api/modules/api-recruitment'
import { setMeeting } from '../../../api/modules/api-meetings'

export const responseHasError = (_response) => {
	if (_response?.status === 'error') {
		if (_response?.action === 'logout') {
			root.users.logout(window.location.pathname + window.location.search)
		}
		return true
	}
	return false
}

/**
 * @async
 * @function actLoadUser
 * @description Loads a user item from the server by its ID and updates the store.
 * @param {string} user_id - The ID of the user item to load.
 * @returns {Promise<void>}
 */
export const actLoadUser = wrapWithLoading(
	async (users, user_id, include_full = false) => {
		const { data } = await getUserByUid(user_id, include_full)
		const hasError = responseHasError(data)
		if (!hasError && data.status === 'success') {
			const _storeItm = users.requireUserById(user_id)
			if (data?.user) {
				runInAction(() => {
					_storeItm.updateFromServer(data.user)
				})
			}
		} else {
			throw data // Throw the error, but only data value
		}
	},
	{ loadingMessage: 'Loading User' }
)

export const actLoadUserDashboardData = wrapWithLoading(
	async () => {
		const { data } = await getUserDashboardData()
		const hasError = responseHasError(data)
		if (!hasError && data.status === 'success') {
			// console.log('actLoadUserDashboardData', data)
			// TODO : setup dashboard data
			const {
				categories
				// , sprints, requests
			} = data
			runInAction(() => {
				root.setCategories(categories)
			})
		} else {
			throw data // Throw the error, but only data value
		}
	},
	{ loadingMessage: 'Loading User Data' }
)

/**
 * @async
 * @function actSaveUser
 * @description Saves a user to the server.
 * @param {string} user_id - The ID of the user to save.
 * @param {Object} full - The full data of the user to save.
 * @returns {Promise<boolean>} True if the save was successful, false otherwise.
 */
export const actSaveUser = wrapWithLoading(
	async (user_id, full, allowed) => {
		await setUser(user_id, full, allowed)
			.then(() => {
				// console.log( 'save user response', response )
			})
			.catch((err) => {
				console.error('save user failed', err)
				throw err
			})
		return true // No exceptions were thrown, so return true
	},
	{ loadingMessage: 'Saving User' }
)

/**
 * @async
 * @function actSendMail
 * @description Sends an email using the provided values and handles loading state.
 * @param {Object} values - The data required to send the email (e.g., email, subject, body).
 * @returns {Promise<boolean>} Resolves to true if the email was sent successfully, false otherwise.
 */
export const actSendMail = wrapWithLoading(
	async (values) => {
		return sendEmail(values)
			.then((response) => {
				// console.log('send email response', response)
				return response
			})
			.catch((err) => {
				console.error('send email failed', err)
				throw err
			})
	},
	{ loadingMessage: 'Sending Mail' }
)

/**
 * @async
 * @function actApplyForJob
 * @description Submits an application for a job using the provided values and handles loading state.
 * @param {Object} values - The data required to apply for the job (e.g., applicant details, job ID).
 * @returns {Promise<boolean>} Resolves to true if the application was submitted successfully,
 * false if the operation failed.
 * The function may throw an error if an unexpected issue occurs during the submission process.
 */
export const actApplyForJob = wrapWithLoading(
	async (id, values) => {
		return setRecruitment(id, values)
			.then((response) => {
				// console.log('actApplyForJob() response', response)
				return response
			})
			.catch((err) => {
				console.error('actApplyForJob() failed', err)
				throw err
			})
	},
	{ loadingMessage: 'Applying...' }
)

/**
 * @async
 * @function actApplyForMeeting
 * @description Schedules a meeting using the provided values and handles loading state.
 * @param {string} id - The ID for the new meeting
 * @param {Object} values - The data required to schedule the meeting
 * @returns {Promise<boolean>} Resolves to true if the meeting was scheduled successfully,
 * false if the operation failed.
 * The function may throw an error if an unexpected issue occurs during the scheduling process.
 */
export const actApplyForMeeting = wrapWithLoading(
	async (id, values) => {
		return setMeeting(id, values)
			.then((response) => {
				console.log('actApplyForMeeting() response', response)
				return response
			})
			.catch((err) => {
				console.error('actApplyForMeeting() failed', err)
				throw err
			})
	},
	{ loadingMessage: 'Scheduling meeting...' }
)

/**
 * @async
 * @function actDeleteUser
 * @description Deletes a user by their user ID.
 * @param {string} userId - The ID of the user to be deleted.
 * @returns {Promise<boolean>} Resolves if the user is deleted successfully, rejects with an error otherwise.
 */
export const actDeleteUser = wrapWithLoading(
	async (userId) => {
		const _response = await deleteUser({
			user_id: userId
		})

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			console.log('user delete response', _response)
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Deleting User' }
)
